import {
  Document,
  Page,
  StyleSheet,
  View,
  Image,
  Text,
  Font,
} from "@react-pdf/renderer";

import { pdfStyles, colors } from "../../config/pdfStyles";
import { tableConfig } from "../../config/tableConfig";
import headerLogo from "../../props/logos/noble240px.png";
import noImageAvailable from "../../props/icons/no-image-available.jpg";
import website from "../../props/icons/website.png";
import email from "../../props/icons/email.png";
import phone from "../../props/icons/phone-call.png";
import iso9001 from "../../props/icons/iso-9001.png";
import bestQuality from "../../props/icons/best-quality.jpg";
import qroLogo from "../../props/icons/qro.png";
import madeInIndia from "../../props/icons/made-in-india.jpg";

const ProductPdf = ({ selectedTable, brandInfo, catalogData }) => {
  // Only register custom font if available
  if (tableConfig.userInfo.fontSource) {
    Font.register({
      family: "Montserrat",
      fonts: [{ src: tableConfig.userInfo.fontSource }],
    });
  }

  // Create StyleSheet from config
  const styles = StyleSheet.create(pdfStyles);

  // ******************* Data Logic *******************
  const numOfRows = selectedTable?.numOfRows || 5;
  const numOfCols = selectedTable?.numOfCols || 6;
  const itemsPerPage = numOfRows * numOfCols;

  // Calculate dynamic dimensions
  const dynamicWidth = `${Math.floor(100 / numOfRows) - 1}%`;
  // Available height for products approx 620
  const dynamicHeight = Math.floor(620 / numOfCols) - 6;
  const imageContainerHeight = Math.max(dynamicHeight - 35, 30);

  // Group data by category and create exact pages
  const createPages = () => {
    if (!catalogData || catalogData.length === 0) {
      return [{ category: null, items: [], showCategoryCard: false }];
    }

    const pages = [];
    let currentCategory = "";
    let currentPageItems = [];
    let slotsUsed = 0;
    let isFirstPageOfCategory = false;

    catalogData.forEach((element) => {
      const category = element?.fields?.category;

      if (currentCategory !== category) {
        if (currentPageItems.length > 0) {
          pages.push({
            category: currentCategory,
            items: [...currentPageItems],
            showCategoryCard: isFirstPageOfCategory,
          });
          currentPageItems = [];
        }
        currentCategory = category;
        isFirstPageOfCategory = true;
        slotsUsed = 1; // Category card takes 1 slot
      } else if (slotsUsed >= itemsPerPage) {
        pages.push({
          category: currentCategory,
          items: [...currentPageItems],
          showCategoryCard: isFirstPageOfCategory,
        });
        currentPageItems = [];
        isFirstPageOfCategory = false;
        slotsUsed = 0;
      }

      currentPageItems.push(element);
      slotsUsed++;
    });

    if (currentPageItems.length > 0) {
      pages.push({
        category: currentCategory,
        items: [...currentPageItems],
        showCategoryCard: isFirstPageOfCategory,
      });
    }

    return pages;
  };

  const renderPageContent = (pageItems, category, showCategoryCard) => {
    const content = [];
    let currentRow = [];
    let itemsInCurrentRow = 0;
    let categoryCardAdded = false;

    pageItems.forEach((element, index) => {
      // Add category card at the beginning of each page (if applicable)
      if (!categoryCardAdded && category && showCategoryCard) {
        currentRow.push(renderCategoryCard(category, `cat-${index}`));
        itemsInCurrentRow = 1;
        categoryCardAdded = true;
      }

      // Add product card
      currentRow.push(renderProductCard(element, `prod-${index}`));
      itemsInCurrentRow++;

      // If row is complete, add to content
      if (itemsInCurrentRow >= numOfRows) {
        content.push(
          <View key={`row-${index}`} style={styles.content}>
            {currentRow}
          </View>,
        );
        currentRow = [];
        itemsInCurrentRow = 0;
      }
    });

    // Add any remaining items in the last row
    if (currentRow.length > 0) {
      // Fill remaining slots with empty space
      while (currentRow.length < numOfRows) {
        currentRow.push(
          <View
            key={`spacer-${currentRow.length}`}
            style={{ width: dynamicWidth }}
          />,
        );
      }
      content.push(
        <View key="final-row" style={styles.content}>
          {currentRow}
        </View>,
      );
    }

    return <View style={styles.contentContainer}>{content}</View>;
  };

  const renderHeader = () => (
    <View style={styles.header}>
      {/* Top Header Row */}
      <View style={styles.headerTop}>
        <View style={styles.headerLeft}>
          <Image src={headerLogo} style={styles.headerLogo} />
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{brandInfo?.companyName}</Text>
            <Text style={styles.companyTagline}>{brandInfo?.tagline}</Text>
          </View>
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.wefDate}>w.e.f. {selectedTable?.wefDate}</Text>
          <Text style={styles.hsnInfo}>HSN Code: {selectedTable?.hsnCode}</Text>
        </View>
      </View>

      {/* Product Heading Section */}
      <View style={styles.productHeadingSection}>
        <Text style={styles.productHeading}>{selectedTable?.displayName}</Text>
        <Text style={styles.productHeadingDescription}>
          {selectedTable?.productDescription}
        </Text>
      </View>
    </View>
  );

  const renderCategoryCard = (category, key) => (
    <View
      style={[
        styles.categoryCard,
        { width: dynamicWidth, height: dynamicHeight },
      ]}
      key={key}
      wrap={false}
    >
      <Text style={styles.categoryTitle} hyphenationCallback={(word) => [word]}>
        {category}
      </Text>
    </View>
  );

  const renderProductCard = (element, key) => {
    // Get the first image with watermark or fallback to first image
    const imageUrl =
      element?.fields?.images && element?.fields?.images.length > 0
        ? // element.fields.images.find((img) => img.filename)?.url ||
          element.fields.images[tableConfig.userInfo.defaultImgIndexToShow]?.url
        : null;

    // Get part number - make it more explicit
    const partNumber = element?.fields?.part_no || element?.fields?.item_code;

    return (
      <View
        style={[
          styles.productCard,
          { width: dynamicWidth, height: dynamicHeight },
        ]}
        key={key}
        wrap={false}
      >
        {/* HSN Code Badge */}
        {element?.fields?.hsn_code && (
          <Text style={styles.hsnCode}>HSN: {element.fields.hsn_code}</Text>
        )}

        {/* Product Image Container */}
        <View
          style={[
            styles.productImageContainer,
            { height: imageContainerHeight },
          ]}
        >
          {/* <Image src={watermarkImg} style={styles.watermarkImage} /> */}

          {/* Part Number - Always show if available */}
          {partNumber && (
            <View style={styles.partNumber}>
              <Text
                style={{
                  fontSize: 9,
                  fontFamily: "Helvetica-Bold",
                  color: colors.white,
                }}
              >
                {partNumber}
              </Text>
            </View>
          )}

          {imageUrl ? (
            <Image src={imageUrl} style={styles.productImage} />
          ) : (
            <Image src={noImageAvailable} style={styles.productImage} />
          )}
        </View>

        {/* Separator Line */}
        <View style={styles.gradientSeparator} />

        {/* Product Information */}
        <View style={styles.productInfo}>
          {element?.fields?.description && (
            <Text style={styles.productDescriptionText}>
              {element.fields.description}
            </Text>
          )}
        </View>
      </View>
    );
  };

  const renderFooter = () => (
    <View style={styles.footer}>
      {/* Orange accent line at top */}
      <View style={styles.footerAccent} />

      {/* Left Section - Company & Basic Contact */}
      <View style={styles.footerLeft}>
        <Text style={styles.footerCompanyName}>
          {tableConfig.userInfo.companyName}
        </Text>
        <View style={styles.footerContactInfo}>
          <View style={styles.contactItem}>
            <Image src={website} style={styles.contactIcon} />
            <Text style={styles.contactText}>{brandInfo?.website}</Text>
          </View>
          <View style={styles.contactItem}>
            <Image src={email} style={styles.contactIcon} />
            <Text style={styles.contactText}>{brandInfo?.emailId}</Text>
          </View>
          <View style={styles.contactItem}>
            <Image src={phone} style={styles.contactIcon} />
            <Text style={styles.contactText}>{brandInfo?.phoneNumber}</Text>
          </View>
        </View>
      </View>

      {/* Center Section - Certifications & HSN */}
      <View style={styles.footerCenter}>
        <Image src={iso9001} style={styles.footerCenterImage} />
        <Image src={bestQuality} style={styles.footerCenterImage} />
        <Image src={qroLogo} style={styles.footerCenterImage} />
        <Image src={madeInIndia} style={styles.footerCenterImage} />
      </View>

      {/* Right Section - Page Number */}
      <View style={styles.footerRight}>
        <View style={styles.pageNumberContainer}>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
            fixed
          />
        </View>
      </View>
    </View>
  );

  // Create pages with category-based page breaks
  const pages = createPages();

  return (
    <Document
      title={`${selectedTable?.displayName} Catalog`}
      author={tableConfig.userInfo.author}
      creator={tableConfig.userInfo.creator}
      producer={tableConfig.userInfo.producer}
      language={tableConfig.userInfo.language}
      pageLayout="singlePage"
    >
      {pages.map((pageData, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page} wrap={true}>
          <View fixed>{renderHeader()}</View>
          {renderPageContent(
            pageData.items,
            pageData.category,
            pageData.showCategoryCard,
          )}
          <View fixed>{renderFooter()}</View>
        </Page>
      ))}
    </Document>
  );
};

export default ProductPdf;
